export const EVENT = {
    name: 'Kitchen Open',
    tagline: "Pickleball's spring major",
    dates: 'April 8–11, 2027',
    datesCompact: '8–11 April 2027',
    venue: 'Mesa Flats Pickleball Park',
    city: 'Palm Desert, California',
    ticketsOnSale: 'January 15, 2027',
    entriesClose: 'March 19, 2027'
} as const

export interface NavLink {
    href: string
    label: string
}

export const NAV_LINKS: NavLink[] = [
    { href: '#the-point', label: 'The point' },
    { href: '#the-week', label: 'The week' },
    { href: '#center-court', label: 'Center court' },
    { href: '#play', label: 'Play' },
    { href: '#faq', label: 'FAQ' }
]

export interface Stat {
    value: number
    suffix?: string
    label: string
}

export const STATS: Stat[] = [
    { value: 560, label: "players across eight doubles divisions" },
    { value: 18, label: "courts, including a 4,000-seat stadium" },
    { value: 4, label: "days of play, Thursday to Sunday" },
    { value: 7, suffix: 'ft', label: "of kitchen on each side of the net, where nobody may volley" }
]

export const SHOT_WORDS: string[] = ['Dink', 'Drop', 'Drive', 'Erne', 'Lob', 'Reset', 'Speed-up']

export interface Shot {
    id: string
    number: string
    short: string
    name: string
    accent: string
    description: string
    fact: string
}

export const SHOTS: Shot[] = [
    {
        id: 'serve',
        number: '01',
        short: 'Serve',
        name: 'The serve',
        accent: 'underhand and deep',
        description: "Every rally starts the same way: an underhand swing, contact below the waist, sent crosscourt past the kitchen line.",
        fact: 'Lands crosscourt, beyond the kitchen'
    },
    {
        id: 'return',
        number: '02',
        short: 'Return',
        name: 'The return',
        accent: 'buy some time',
        description: "The serve has to bounce, and so does the return. Hit it deep and high, then sprint to the kitchen line while it floats.",
        fact: 'Two-bounce rule: no volleys yet'
    },
    {
        id: 'drop',
        number: '03',
        short: 'Drop',
        name: 'The third-shot drop',
        accent: 'the shot that matters',
        description: "The serving team is stuck at the back. A soft, high arc that dies in the other kitchen buys them time to come forward.",
        fact: 'Peaks early, falls into the kitchen'
    },
    {
        id: 'dink',
        number: '04',
        short: 'Dink',
        name: 'The dink',
        accent: 'patience, crosscourt',
        description: "All four players at the line. Nobody can volley from inside the kitchen, so the ball stays low and soft until someone blinks.",
        fact: 'Low over the net, soft into the kitchen'
    },
    {
        id: 'putaway',
        number: '05',
        short: 'Put-away',
        name: 'The put-away',
        accent: 'and that is the point',
        description: "One dink floats an inch too high. Step up, swing down and aim between them.",
        fact: 'Attack the high ball, down the middle'
    }
]

export interface Session {
    day: string
    date: string
    slot: string
    title: string
    description: string
    where: string
}

export const SESSIONS: Session[] = [
    {
        day: 'Thu',
        date: '08',
        slot: 'Day',
        title: 'Amateur pool play',
        description: "The 3.5, 4.0, 4.5+ and senior divisions open on the outer courts.",
        where: 'Courts 1–16 · 8:00 AM'
    },
    {
        day: 'Thu',
        date: '08',
        slot: 'Night',
        title: 'Opening night',
        description: "Skills challenge, the pro draw ceremony and a DJ who takes requests.",
        where: 'Center Court · 7:00 PM'
    },
    {
        day: 'Fri',
        date: '09',
        slot: 'Day',
        title: 'Pro round of 32',
        description: "Men's, women's and mixed doubles on every court at once.",
        where: 'All courts · 9:00 AM'
    },
    {
        day: 'Fri',
        date: '09',
        slot: 'Night',
        title: 'Round of 16',
        description: "The top seeds take Center Court under the lights.",
        where: 'Center Court · 7:00 PM'
    },
    {
        day: 'Sat',
        date: '10',
        slot: 'Day',
        title: 'Amateur medal matches',
        description: "Gold, silver and bronze in every amateur and senior division.",
        where: 'Grandstand · 9:00 AM'
    },
    {
        day: 'Sat',
        date: '10',
        slot: 'Night',
        title: 'Pro quarterfinals',
        description: "Four matches, one court, no empty seats.",
        where: 'Center Court · 6:30 PM'
    },
    {
        day: 'Sun',
        date: '11',
        slot: 'Day',
        title: 'Semifinals',
        description: "The last four teams in every pro division.",
        where: 'Center Court · 10:00 AM'
    },
    {
        day: 'Sun',
        date: '11',
        slot: 'Finals',
        title: 'Finals',
        description: "Mixed at 1:00, women's at 3:30 and men's at 6:00, with the trophy right after.",
        where: 'Center Court · 1:00 PM'
    }
]

export interface VenueFact {
    value: string
    label: string
}

export const VENUE_FACTS: VenueFact[] = [
    { value: '4,000', label: 'seats around Center Court' },
    { value: '1,200', label: 'more in the Grandstand' },
    { value: '16', label: 'outer courts, free with any pass' },
    { value: '12', label: "kitchens on food row. The other kind." }
]

export type DivisionGroup = 'Pro' | 'Amateur'

export interface Division {
    id: string
    name: string
    group: DivisionGroup
    draw: number
    fee: number
    prize: string
    eligibility: string
}

export const DIVISIONS: Division[] = [
    { id: 'pro-mens', name: "Pro men's doubles", group: 'Pro', draw: 32, fee: 180, prize: '$40,000 purse', eligibility: 'Ranked pros and DUPR 5.0+' },
    { id: 'pro-womens', name: "Pro women's doubles", group: 'Pro', draw: 32, fee: 180, prize: '$40,000 purse', eligibility: 'Ranked pros and DUPR 5.0+' },
    { id: 'pro-mixed', name: 'Pro mixed doubles', group: 'Pro', draw: 32, fee: 180, prize: '$30,000 purse', eligibility: 'Ranked pros and DUPR 5.0+' },
    { id: 'amateur-35', name: 'Amateur 3.5 doubles', group: 'Amateur', draw: 48, fee: 90, prize: 'Medals', eligibility: 'DUPR 3.0 to 3.99' },
    { id: 'amateur-40', name: 'Amateur 4.0 doubles', group: 'Amateur', draw: 48, fee: 90, prize: 'Medals', eligibility: 'DUPR 4.0 to 4.49' },
    { id: 'amateur-45', name: 'Amateur 4.5+ doubles', group: 'Amateur', draw: 32, fee: 110, prize: 'Medals', eligibility: 'DUPR 4.5 and up' },
    { id: 'senior-50', name: '50+ doubles', group: 'Amateur', draw: 32, fee: 90, prize: 'Medals', eligibility: 'Both players 50 or older' },
    { id: 'senior-65', name: '65+ doubles', group: 'Amateur', draw: 24, fee: 90, prize: 'Medals', eligibility: 'Both players 65 or older' }
]

export interface Pass {
    id: string
    name: string
    price: number
    unit: string
    description: string
    perks: string[]
    featured?: boolean
}

export const PASSES: Pass[] = [
    {
        id: 'grounds',
        name: 'Grounds pass',
        price: 25,
        unit: 'per day',
        description: "Wander every outer court and watch the amateur divisions up close.",
        perks: ['Courts 1–16 and the Grandstand', 'Morning beginner clinic, paddles provided', 'Food row and the fan village']
    },
    {
        id: 'session',
        name: 'Center Court session',
        price: 65,
        unit: 'per session',
        description: "A reserved stadium seat for one day or night session, plus grounds access that day.",
        perks: ['Reserved seat on Center Court', 'Any single day or night session', 'Grounds pass included']
    },
    {
        id: 'kitchen',
        name: 'Kitchen pass',
        price: 189,
        unit: 'all four days',
        description: "The same reserved seat for all eight sessions and a shaded lounge right by the courts.",
        perks: ['Your seat for every session', 'The Kitchen Lounge, shaded and stocked', 'Finals Sunday trophy walk'],
        featured: true
    }
]

export interface Faq {
    question: string
    answer: string
}

export const FAQS: Faq[] = [
    {
        question: "I've never played pickleball. Will I enjoy it?",
        answer: "Yes. It's the easiest racket sport to follow, and every grounds pass includes a free 30-minute beginner clinic each morning with paddles provided."
    },
    {
        question: 'How do team entries work?',
        answer: "Pick a division and enter both names. Spots are confirmed in the order entries arrive, and the tournament desk emails every team to confirm its place and collect the entry fee. Once a draw is full, new entries join its waitlist."
    },
    {
        question: 'Which ratings do the amateur divisions use?',
        answer: "We seed amateur divisions by DUPR. No rating yet? Leave that field empty and we'll place your team after a short check-in."
    },
    {
        question: 'When do passes go on sale?',
        answer: "January 15, 2027. Everyone on the ticket list gets a 48-hour head start before general sale."
    },
    {
        question: 'Is there shade?',
        answer: "Every outer court has shade sails, the fan village is covered and the Kitchen Lounge is fully shaded. It's still the desert, so bring sunscreen and a hat."
    },
    {
        question: 'Can I bring my own paddle and balls?',
        answer: "Paddles, yes. Balls, no. Every match uses the official tournament outdoor ball."
    }
]
